import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/apiConfig';

const REDIRECT_SECONDS = 8;

const SocialConnect: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Completing social platform connection...');
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  const params = useMemo(
    () => ({
      code: searchParams.get('code') || '',
      state: searchParams.get('state') || '',
      error: searchParams.get('error') || '',
    }),
    [searchParams]
  );

  useEffect(() => {
    let mounted = true;

    const completeOAuth = async () => {
      if (params.error) {
        if (!mounted) return;
        setStatus('error');
        setMessage(params.error);
        return;
      }

      if (!params.code || !params.state) {
        if (!mounted) return;
        setStatus('error');
        setMessage('Missing OAuth callback parameters.');
        return;
      }

      try {
        const qs = new URLSearchParams({ code: params.code, state: params.state });
        const response = await fetch(`${getApiBaseUrl()}/api/v1/social-media/oauth/callback?${qs.toString()}`, {
          method: 'GET',
          credentials: 'include'
        });
        const body = await response.json();
        if (!mounted) return;
        if (response.ok) {
          setStatus('success');
          setMessage(`Social platform connected${body?.connection?.platform ? `: ${body.connection.platform}` : ''}.`);
        } else {
          setStatus('error');
          setMessage(body?.message || 'Failed to complete social OAuth connection.');
        }
      } catch {
        if (!mounted) return;
        setStatus('error');
        setMessage('Could not reach the backend callback endpoint.');
      }
    };

    completeOAuth();
    return () => {
      mounted = false;
    };
  }, [params.code, params.error, params.state]);

  useEffect(() => {
    if (status === 'processing') return;
    if (secondsLeft <= 0) {
      navigate('/admin/dashboard?tab=socialMedia', { replace: true });
      return;
    }
    const timer = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [status, secondsLeft, navigate]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 520, background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>
          {status === 'processing' ? 'Connecting Platform' : status === 'success' ? 'Connection Completed' : 'Connection Error'}
        </h1>
        <p style={{ marginTop: 12 }}>{message}</p>
        {status !== 'processing' && (
          <p style={{ marginTop: 16, color: '#6b7280' }}>Redirecting to admin social page in {secondsLeft}s...</p>
        )}
      </div>
    </div>
  );
};

export default SocialConnect;

